import { useMemo, useState } from 'react';
import type { TemplateVariable } from '../hooks/useDocumentTemplates';

interface FieldsPanelProps {
  variables: TemplateVariable[];
  /** Insert a field at the caret. `key` is the raw variable key (may end in `[]`). */
  onInsert: (variable: TemplateVariable) => void;
}

/** Friendly section titles for the raw `source` values the API returns. */
const SOURCE_LABELS: Record<string, string> = {
  billing: 'Invoice / Billing',
  contacts: 'Client',
  branding: 'Firm',
  system: 'System',
  report: 'Report',
};

function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] || source.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * FieldsPanel — searchable, grouped list of template fields that can be dropped
 * straight into the visual canvas at the caret. This is the visual-mode
 * equivalent of the code-mode VariableInserter, so users no longer have to drop
 * to raw Handlebars to add a field.
 */
export function FieldsPanel({ variables, onInsert }: FieldsPanelProps) {
  const [search, setSearch] = useState('');

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? variables.filter(
          (v) => v.key.toLowerCase().includes(term) || v.label.toLowerCase().includes(term),
        )
      : variables;

    const bySource = new Map<string, TemplateVariable[]>();
    for (const v of filtered) {
      const src = v.source || 'other';
      const list = bySource.get(src) ?? [];
      list.push(v);
      bySource.set(src, list);
    }
    return Array.from(bySource.entries());
  }, [variables, search]);

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search fields…"
          aria-label="Search fields"
          className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        />
        <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
          Click a field to drop it where your cursor is.
        </p>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
        {groups.length === 0 ? (
          <p className="px-1 py-2 text-xs text-gray-500 dark:text-gray-400">No fields match.</p>
        ) : (
          groups.map(([source, vars]) => (
            <div key={source}>
              <h4 className="mb-1 px-0.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {sourceLabel(source)}
              </h4>
              <div className="flex flex-wrap gap-1">
                {vars.map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    // Keep focus (and the caret) in the canvas while clicking.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onInsert(v)}
                    title={v.description || v.key}
                    className="max-w-full truncate rounded-full border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] font-medium text-indigo-700 transition-colors hover:border-indigo-400 hover:bg-indigo-100 dark:border-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-900/50"
                  >
                    {v.label}
                    {v.key.endsWith('[]') && (
                      <span className="ml-1 text-[9px] text-indigo-400">list</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
